import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AlertTriangle,
  Check,
  Film,
  GitCommitHorizontal,
} from "lucide-react";
import { api } from "../api";
import type { VersionControlSnapshot } from "../types";

interface VideoVersionHistoryProps {
  productId: string;
  versionControl: VersionControlSnapshot;
  onUpdated: () => Promise<unknown>;
}

function formatTimestamp(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

export function VideoVersionHistory({
  productId,
  versionControl,
  onUpdated,
}: VideoVersionHistoryProps) {
  const selectedFinal =
    versionControl.selected_final_video_version_id ||
    versionControl.video_versions[0]?.video_version_id ||
    "";
  const [previewId, setPreviewId] = useState(selectedFinal);

  const preview = useMemo(
    () =>
      versionControl.video_versions.find(
        (item) => item.video_version_id === previewId,
      ) ?? versionControl.video_versions[0],
    [previewId, versionControl.video_versions],
  );

  const selectMutation = useMutation({
    mutationFn: (videoVersionId: string) =>
      api.selectFinalVideoVersion(productId, videoVersionId),
    onSuccess: async () => {
      await onUpdated();
    },
  });

  if (!preview) return null;

  return (
    <section className="video-version-history">
      <header className="video-version-heading">
        <div>
          <span className="document-label">FINAL VIDEO / VERSION CONTROL</span>
          <h3>成片版本库</h3>
          <p>历史成片永久保留，可随时预览并重新设为最终采用版。</p>
        </div>
        <div className="selected-final-chip">
          <Check size={14} aria-hidden="true" />
          当前采用 {selectedFinal}
        </div>
      </header>

      <div className="video-version-layout">
        <div className="video-version-preview">
          <video
            key={preview.media_url}
            controls
            preload="metadata"
            src={preview.media_url}
          >
            {preview.caption_url && (
              <track
                default
                kind="subtitles"
                src={preview.caption_url}
                srcLang="zh"
                label="中文字幕"
              />
            )}
            当前浏览器不支持视频播放。
          </video>
          <div className="video-preview-meta">
            <div>
              <strong>{preview.label}</strong>
              <span>
                {preview.video_version_id} ·{" "}
                {preview.shot_count ? `${preview.shot_count} 镜头` : "成片"} ·{" "}
                {preview.duration ? `${preview.duration}s` : "时长读取中"}
              </span>
            </div>
            {!preview.based_on_current_script && (
              <span className="stale-script-chip">
                <AlertTriangle size={13} aria-hidden="true" />
                基于旧脚本
              </span>
            )}
          </div>
        </div>

        <aside className="video-version-list">
          {versionControl.video_versions.map((item) => (
            <article
              key={item.video_version_id}
              className={item.video_version_id === previewId ? "is-active" : ""}
            >
              <button
                type="button"
                className="video-version-select"
                onClick={() => setPreviewId(item.video_version_id)}
              >
                <Film size={16} aria-hidden="true" />
                <div>
                  <span>
                    {item.video_version_id}
                    {item.is_selected_final && <em>最终采用</em>}
                  </span>
                  <strong>{item.label}</strong>
                  <small>
                    {formatTimestamp(item.created_at)} ·{" "}
                    {item.script_revision_id || "初始脚本"}
                  </small>
                </div>
              </button>
              {!item.is_selected_final && (
                <button
                  type="button"
                  className="adopt-video-version"
                  disabled={selectMutation.isPending}
                  onClick={() => selectMutation.mutate(item.video_version_id)}
                >
                  <GitCommitHorizontal size={13} aria-hidden="true" />
                  设为最终采用
                </button>
              )}
            </article>
          ))}
        </aside>
      </div>

      {selectMutation.isError && (
        <p className="version-feedback is-error">
          <AlertTriangle size={15} aria-hidden="true" />
          {selectMutation.error.message}
        </p>
      )}
    </section>
  );
}
